/**
 * The browser's `expo/fetch`: the global fetch, same origin, with the cookie jar.
 *
 * The phone names `expo/fetch` so the request survives React Native's own
 * fetch being swapped in; the browser has one fetch and it already streams.
 *
 * One difference is kept on purpose. `expo/fetch` rejects a request that never
 * reached a server with a `FetchError`, and the phone's failure classifier
 * (`apps/mobile/src/lib/sync-failure.ts`) reads that name as "unreachable"
 * while it reads a bare `TypeError` as a bug. The browser rejects the same
 * failure with a `TypeError`, so it is renamed here and the classifier holds
 * for both.
 *
 * Every call ends here, so here is where the browser says who it is:
 * `x-alluzo-client: web/<version>/<features>` (web-redesign-02 phase 1). The
 * phone's `@/lib/client-fetch` runs above this and names the phone; this
 * replaces that value, because in a browser the caller is the web app.
 */
import { clientHeaderValue, withClientHeader, type ClientFeature } from '@alluzo/shared';

import { noteApiAnswered, noteApiUnreachable } from '~/lib/online-state';

import { version } from '../../package.json';

/**
 * What the web app understands. `roles`: it draws Roles, a person's access and
 * Reports to (web-redesign-03), and OS's role and access cards (web-redesign-08
 * phase 7), so OS offers it the role tools and kinds rather than the old jobs
 * and levels. `set_view`: the record page applies a view OS sets
 * (os-live-mode.md 3.1). `fill_entry`: the new-entry page takes what OS fills
 * into it (web-redesign-10 phase 7). `team_cards`: it draws OS's phase-3 team
 * cards (roles-parity 3.7), so OS offers it the tools that make them.
 * `doc_templates` and `business_details`: it draws report templates and the
 * business detail card (report-templates.md D24), which the phone never sees.
 * `connectors`: it has the Connectors pages and draws OS's email and rule
 * cards (docs/plans/connectors-gmail.md Decided 17), so OS offers it the
 * Gmail tools. `kits`: it draws OS's kit card (field-relations Phase 8), so
 * OS offers it add_kit. `approvals` and `money`: it draws OS's approve and
 * send-back card and its money cards (close a month, a charge run, a
 * correction, a payment moved, an approval chain; Phase 12 of
 * docs/plans/ledger.md), so OS offers it the tools that make them.
 * `ledger_cards`: it pins and draws a money figure on the Dashboard (step
 * 12.5; `drawLedgerTiles` in main.tsx), so propose_card may offer one.
 * `task_rules`: it draws OS's task rule card and a record's task rules (step
 * 9.7 of docs/plans/ledger.md), so OS offers it propose_task_rule.
 * `repairs`: it draws OS's repair cards, a setup fix and Ask the owner, and
 * the Dashboard's Things to fix (L64 of docs/plans/ledger.md), so OS offers
 * it the repair tools rather than only refusing.
 */
const WEB_FEATURES: readonly ClientFeature[] = [
  'roles',
  'set_view',
  'fill_entry',
  'team_cards',
  'doc_templates',
  'business_details',
  'connectors',
  'kits',
  'approvals',
  'money',
  'ledger_cards',
  'task_rules',
  'repairs',
];

export const WEB_CLIENT = clientHeaderValue('web', version, WEB_FEATURES);

class FetchError extends Error {
  override name = 'FetchError';
}

export const fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  try {
    const sent: RequestInit = { credentials: 'same-origin', ...init };
    const answered = await globalThis.fetch(input, withClientHeader(sent, WEB_CLIENT));
    // Any answer proves the connection, whatever the browser says (G8; `online-state.ts`).
    noteApiAnswered();
    return answered;
  } catch (error) {
    if (error instanceof TypeError) {
      noteApiUnreachable();
      throw new FetchError(error.message, { cause: error });
    }
    throw error;
  }
};
